import clsx from "clsx";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isToday from "date-fns/isToday";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useState } from "react";

import Comment from "@material-ui/icons/Comment";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import type { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScheduleDateMenu from "components/ScheduleDateMenu/ScheduleDateMenu";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useMutationWithRefetch } from "hooks/useMutationWithRefetch";
import ICalendarDay from "interfaces/ICalendarDay";
import { shouldShutOffDate } from "utils/date-utils";
import { WORKOUT_REQUEST_GET_OPTIONS } from "../../options";

import useStyles from "./styles";

enum EDataTestState {
  AVAILABLE = "AVAILABLE",
  UNAVAILABLE = "UNAVAILABLE",
  SCHEDULED = "SCHEDULED"
}

interface IProps {
  dayDate: string;
  workoutRequest?: racehorse360.IWorkoutRequest;
  currentHorseId?: string;
  className?: string;
  facilityBlackoutsCalendar?: ICalendarDay[];
  refetchData: () => Promise<void>;
}

const ScheduleDay = (props: IProps) => {
  const {
    dayDate,
    refetchData,
    workoutRequest,
    currentHorseId,
    facilityBlackoutsCalendar = [],
    className,
    ...restProps
  } = props;

  const { currentUser } = useLoggedInUser();

  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLDivElement>(null);
  const [activeWorkoutRequest, setActiveWorkoutRequest] =
    useState<racehorse360.IWorkoutRequest>(workoutRequest);

  const classes = useStyles({
    backgroundColor: activeWorkoutRequest?.facility?.backgroundColor,
    strokeColor: activeWorkoutRequest?.facility?.strokeColor
  });

  const selectedFacility = useAppSelector(
    state => state.workoutsPage.selectedFacility
  );

  const facility = activeWorkoutRequest?.facility;
  const facilityCode = facility?.code.trim() || EMPTY_STRING;

  const hasWorkoutRequest = Boolean(activeWorkoutRequest);
  const date = parseISO(dayDate);

  const isFacilityBlackout = facilityBlackoutsCalendar.some(
    day => isSameDay(day.date, date) && day.facilityBlackout
  );
  const isShutOffDate =
    selectedFacility && shouldShutOffDate(date, selectedFacility);
  const shouldDisableDate =
    !hasWorkoutRequest && [isFacilityBlackout, isShutOffDate].some(Boolean);

  const shouldShowScheduleDateMenu = [
    isDateMenuOpen,
    activeWorkoutRequest
  ].every(Boolean);

  let dataTestState = EDataTestState.AVAILABLE;
  if (hasWorkoutRequest) {
    dataTestState = EDataTestState.SCHEDULED;
  } else if (!hasWorkoutRequest && shouldDisableDate) {
    dataTestState = EDataTestState.UNAVAILABLE;
  }

  useEffect(() => {
    setActiveWorkoutRequest(workoutRequest);
  }, [workoutRequest]);

  const {
    useCreateWorkoutRequest,
    useCancelWorkoutRequest,
    useMoveFacilityWorkoutRequest
  } = useRacehorse360Api();

  const { mutateAsync: createWorkoutRequest } = useCreateWorkoutRequest();
  const { mutateAsync: cancelWorkoutRequest } = useCancelWorkoutRequest();
  const { mutateAsync: moveFacilityWorkoutRequest } =
    useMoveFacilityWorkoutRequest();

  const moveFacilityWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: moveFacilityWorkoutRequest,
    refetch: refetchData
  });

  const cancelWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: cancelWorkoutRequest,
    refetch: refetchData
  });

  const createWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: createWorkoutRequest,
    refetch: refetchData
  });

  const isLoading = [
    moveFacilityWorkoutRequestTransaction.isLoading,
    cancelWorkoutRequestTransaction.isLoading,
    createWorkoutRequestTransaction.isLoading
  ].some(Boolean);

  const handleDateMenuClose = () => {
    setIsDateMenuOpen(false);
  };

  const handleCardClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (createWorkoutRequestTransaction.isLoading) return;

    if (hasWorkoutRequest) {
      setDateMenuAnchor(event.currentTarget);
      setIsDateMenuOpen(true);
    } else {
      const createdWorkoutRequest = await createWorkoutRequestTransaction.run({
        date: dayDate,
        facilityId: selectedFacility.id,
        horseId: currentHorseId,
        getOptions: {
          select: WORKOUT_REQUEST_GET_OPTIONS
        }
      });

      setActiveWorkoutRequest(createdWorkoutRequest);
    }
  };

  const handleChangeWorkoutRequest = async (
    workoutData: racehorse360.IWorkoutRequest
  ) => {
    if (
      cancelWorkoutRequestTransaction.isLoading ||
      moveFacilityWorkoutRequestTransaction.isLoading
    )
      return;

    if (workoutData.facility) {
      const updatedWorkoutRequest =
        await moveFacilityWorkoutRequestTransaction.run({
          id: workoutData.id,
          facilityId: workoutData.facility.id,
          getOptions: {
            select: WORKOUT_REQUEST_GET_OPTIONS
          }
        });

      setActiveWorkoutRequest(updatedWorkoutRequest);
    } else {
      await cancelWorkoutRequestTransaction.run({ id: workoutData.id });
      setActiveWorkoutRequest(null);
    }
  };

  const renderCommentIcon = () => {
    const hasComment = currentUser.isRacingOfficial
      ? activeWorkoutRequest.hasRacingSecretaryComment
      : activeWorkoutRequest.hasComment;

    if (hasComment) {
      return <Comment className={classes.commentIcon} />;
    }
  };

  return (
    <>
      <div
        role={"button"}
        className={clsx(classes.card, className, classes[facility?.code], {
          scheduled: hasWorkoutRequest,
          [classes.cardDisabled]: shouldDisableDate
        })}
        onClick={handleCardClick}
        {...restProps}
        data-test-state={dataTestState}
      >
        {isLoading && <Loader className={classes.loader} />}
        <p className={clsx(classes.dayName, { scheduled: hasWorkoutRequest })}>
          {format(date, "E").toUpperCase()}
        </p>
        <div
          className={clsx(classes.cardHeader, {
            scheduled: hasWorkoutRequest
          })}
        >
          {facilityCode}
        </div>
        <div
          className={clsx(classes.cardContent, {
            scheduled: hasWorkoutRequest
          })}
        >
          <div className={clsx({ [classes.dateWrapper]: hasWorkoutRequest })}>
            <p
              className={clsx(classes.date, {
                scheduled: hasWorkoutRequest,
                [classes.squared]: isToday(date)
              })}
            >
              {date.getDate()}
            </p>
          </div>
        </div>

        {hasWorkoutRequest && (
          <div className={classes.cardActions}>
            <MoreHorizIcon className={classes.moreHorizIcon} />

            {renderCommentIcon()}
          </div>
        )}
      </div>

      {shouldShowScheduleDateMenu && (
        <ScheduleDateMenu
          horse={activeWorkoutRequest.horse}
          anchorEl={dateMenuAnchor}
          workoutData={activeWorkoutRequest}
          onChange={handleChangeWorkoutRequest}
          onClose={handleDateMenuClose}
          isOpen={isDateMenuOpen}
        />
      )}
    </>
  );
};

export default React.memo(ScheduleDay);
